import { HttpStatusCode } from 'axios';

import { useAuthStore } from '@/store';
import { Role } from '@/const.js';
import { useHttp } from '@/composables';

export default async to => {
  const authStore = useAuthStore();

  // this checks if it or any of its parents has the meta specified
  const routeRequiresAuth = to.matched.some(r => r.meta.requiresAuthentication);
  const routeRequiresAdmin = to.matched.some(r => r.meta.requiresAdmin);
  const routeRedirectsAuth = to.matched.some(r => r.meta.redirectAuthenticatedUsers);

  // load authStore if needed for determination of perms
  if ((routeRequiresAuth || routeRedirectsAuth) && !authStore.isLoaded) {
    authStore.loading = true;

    const response = await useHttp().get('/profile');

    if (response.statusCode === HttpStatusCode.Ok) {
      authStore.user = response.data;
    }

    authStore.isLoaded = true;
    authStore.loading = false;
  }

  const isAuthenticated = authStore.isAuthenticated;

  // Deep-links (e.g. the "Log in to Review" button in notification emails) carry a target
  // municipality in the URL. The UI is scoped to the user's stored "current" municipality, so
  // when the URL targets a different one the user belongs to, switch into it before the page
  // loads — otherwise the page renders the previously-selected municipality's plan.
  if (isAuthenticated && to.params.municipality) {
    const targetId = Number(to.params.municipality);
    const memberships = authStore.user?.municipalities ?? [];
    const target = memberships.find(m => m.id === targetId);

    // Only switch when the user is a member of the target and it isn't already current.
    if (target && !target.current) {
      const response = await useHttp().put('/profile/municipality', { currentMunicipality: targetId });

      if (response.statusCode === HttpStatusCode.Ok) {
        authStore.user = response.data;
      }
    }
  }

  const currentMunicipality = authStore.user?.municipalities?.find(m => m.current);

  // define default user landing page
  const userDefaultLanding = !isAuthenticated ?
    { name: 'app_auth_login' } :
    currentMunicipality
      ? { name: 'app_plan_overview', params: { municipality: currentMunicipality.id } }
      : { name: 'app_municipality_select' };

  // user tries to go to '/'
  if (to.path === '/') return userDefaultLanding;

  // for unauthenticated users, redirect if route requires auth otherwise let them thru
  if (!isAuthenticated) return routeRequiresAuth ? userDefaultLanding : true;

  // stop authenticated users going to unauthenticated routes
  if (isAuthenticated && routeRedirectsAuth) return userDefaultLanding;

  // if the route specifies allowed roles
  const routeAllowedRoles = to.meta.allowedRoles || [];
  const userRoles = authStore.user?.roles || [];

  // admins can see any page
  if (userRoles.includes(Role.SystemAdministrator)) return true;

  // deny access if user doesnt have atleast one of the roles listed. An admin route
  // lists the roles it also opens to, so with none listed it stays admin only
  const hasAllowedRole = routeAllowedRoles.some(role => userRoles.includes(role));

  if ((routeRequiresAdmin || routeAllowedRoles.length > 0) && !hasAllowedRole) {
    return userDefaultLanding;
  }

  // if at this point, the route allows any authorized user, so let them thru
  return true;
};

// System Administrator - technical super-user responsible for the overall management and maintenance of the platform. Manages the list of municipalities. Creates and manages user accounts and assigns roles. Manages the pre-loaded frameworks. Is able to edit IDP content for the municipalities that they have been linked to.
// Municipal Administrator - possesses all permissions of a Data Capturer (IDP Officer). This role can Approve or Reject IDPs at review stages and has the sole authority to Publish an approved IDP. Can add IDP Officers to their own municipality.
// IDP Officer - creates and edits all sections of an IDP in Draft status. Manages and uploads annexures. Enters project data, financial data, and SDBIP indicators. Submits the IDP for review.
// Viewer - able to see Draft documents, but without the ability to edit them. They see the pdf version.
